import type {BlockInstance} from '../appearance-data';
import {Reveal} from '../motion';
import {accent} from './accents';
import {Section, SectionHeading} from './section-kit';

interface FeatureItem {
  icon?: string;
  image?: string;
  title?: string;
  text?: string;
}

/** Features grid. `items` is a localized repeater. */
export function FeaturesBlock({props}: {block: BlockInstance; props: Record<string, any>}) {
  const items: FeatureItem[] = Array.isArray(props.items) ? props.items : [];
  const cols = props.columns === '4' ? 'md:grid-cols-4' : props.columns === '2' ? 'md:grid-cols-2' : 'md:grid-cols-3';

  // "Marquee" style turns the grid into a continuously scrolling band — ideal
  // for trust/partner logos, and it adds motion to an otherwise static wall of
  // cards. The list is duplicated so the loop is seamless at -50%.
  if (props.layout === 'marquee' && items.length) {
    const isRtl = document.documentElement.dir === 'rtl';
    const loop = [...items, ...items];
    return (
      <section className="relative isolate overflow-hidden px-0 py-64">
        <div className="mx-auto mb-36 max-w-[1100px] px-24">
          <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} />
        </div>
        {/* Edge fade so items enter/leave softly instead of being clipped. */}
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-64 bg-gradient-to-r from-[var(--be-background,#fff)] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-64 bg-gradient-to-l from-[var(--be-background,#fff)] to-transparent" />
          <div
            className="flex w-max items-stretch gap-20 hover:[animation-play-state:paused]"
            style={{
              animationName: isRtl ? 'ap-marquee-rtl' : 'ap-marquee',
              animationDuration: `${Math.max(18, items.length * 5)}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
            }}
          >
            {loop.map((item, i) => (
              <div key={i}
                className="ap-lift flex w-240 shrink-0 items-center gap-12 rounded-2xl border border-divider bg-paper px-20 py-16 shadow-sm">
                {item.image ? (
                  <img src={item.image} alt="" className="h-40 w-40 shrink-0 object-contain" />
                ) : item.icon ? (
                  <span className="flex h-40 w-40 shrink-0 items-center justify-center rounded-xl"
                    style={{backgroundColor: accent(i).bg, color: accent(i).fg}}>
                    <span className="material-icons text-2xl">{item.icon}</span>
                  </span>
                ) : null}
                <div className="min-w-0">
                  {item.title ? <h3 className="truncate text-sm font-semibold">{item.title}</h3> : null}
                  {item.text ? <p className="truncate text-xs text-muted">{item.text}</p> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Keyframes are defined locally so this block works standalone. */}
        <style>{`@keyframes ap-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}@keyframes ap-marquee-rtl{from{transform:translateX(-50%)}to{transform:translateX(0)}}`}</style>
      </section>
    );
  }

  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className={`grid grid-cols-1 gap-24 ${cols}`}>
          {items.map((item, i) => (
            <Reveal key={i} index={i}>
              <div className="ap-lift ap-shine h-full rounded-2xl border border-divider bg-paper p-24 text-center shadow-sm transition hover:border-primary hover:shadow-lg">
                {item.image ? (
                  <img src={item.image} alt="" className="mx-auto mb-14 h-56 w-56 object-contain" />
                ) : item.icon ? (
                  <span className="mx-auto mb-14 flex h-56 w-56 items-center justify-center rounded-2xl"
                    style={{backgroundColor: accent(i).bg, color: accent(i).fg}}>
                    <span className="material-icons text-3xl">{item.icon}</span>
                  </span>
                ) : null}
                {item.title ? <h3 className="mb-8 text-lg font-semibold">{item.title}</h3> : null}
                {item.text ? <p className="text-sm text-muted">{item.text}</p> : null}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}
